import { PermissionScope } from "@/modules/auth";
import type { Firm } from "@/modules/firms";
import type {
  Operator as AccountsOperator,
  OperatorCreate,
  OperatorUpdate,
} from "@/providers/smarta-accounts/accounts-api-client";

/**
 * Operator record from smarta-accounts. Re-exported from the generated accounts
 * client (the source of truth) so call sites talk to the same shape the API
 * returns — the operator surface is consumed through `modules/integrations/accounts`,
 * the same way banking consumes its generated client.
 */
export type Operator = AccountsOperator;

export type OperatorCreatePayload = OperatorCreate;

export type OperatorUpdatePayload = OperatorUpdate;

/**
 * Backend operators only ever return one of these 5 scopes. The wider
 * `PERMISSION_SCOPES` enum (which also includes legacy DIY / INVALID) is
 * kept on the client for runtime guards, but the operator-management
 * dropdown must only show the 5 backend values.
 */
export const OPERATOR_PERMISSION_SCOPES = [
  "TAX_SUPERVISOR",
  "ADMIN",
  "CLERK",
  "ACCOUNTING",
  "READONLY",
] as const satisfies PermissionScope[];

/**
 * Local-only sentinel used as the row id while an "Add new" form is
 * staged but not yet persisted. Real operator ids from smarta-accounts
 * never start with two underscores.
 */
export const NEW_OPERATOR_SENTINEL = "__new__";

export type OperatorStatus = "enabled" | "disabled";

/**
 * One row of the operator management table. The person's name fields sit on
 * the row itself, so the editable table can bind each one to its own input.
 * An empty optional name is an empty string here and `null` in the API.
 * `displayName` is the name smarta-accounts derives; the table shows it and
 * never builds a name from the parts.
 */
export interface OperatorRow {
  id: string;
  displayName: string;
  email: string;
  givenNames: string;
  familyName: string;
  preferredName: string;
  firm: Firm;
  permissionScope: Operator["permissionScope"];
  status: OperatorStatus;
}

export const toOperatorRow = (operator: Operator): OperatorRow => ({
  id: operator.id,
  displayName: operator.person.displayName,
  email: operator.person.email ?? "",
  givenNames: operator.person.givenNames ?? "",
  familyName: operator.person.familyName,
  preferredName: operator.person.preferredName ?? "",
  firm: operator.firm,
  permissionScope: operator.permissionScope,
  status: operator.disabledAt === null ? "enabled" : "disabled",
});

const optionalName = (value?: string): string | null => value?.trim() || null;

export const toOperatorCreatePayload = (
  row: OperatorRow
): OperatorCreatePayload => {
  const givenNames = optionalName(row.givenNames);
  const preferredName = optionalName(row.preferredName);
  return {
    permissionScope: row.permissionScope,
    firm: row.firm,
    person: {
      email: row.email.trim(),
      familyName: row.familyName.trim(),
      ...(givenNames ? { givenNames } : {}),
      ...(preferredName ? { preferredName } : {}),
    },
  };
};

/*
 * An update sends every name field, so a cleared optional name becomes `null`
 * and clears it in smarta-accounts. The email is not editable after create.
 */
export const toOperatorUpdatePayload = (
  row: OperatorRow
): OperatorUpdatePayload => ({
  permissionScope: row.permissionScope,
  firm: row.firm,
  person: {
    givenNames: optionalName(row.givenNames),
    familyName: row.familyName.trim(),
    preferredName: optionalName(row.preferredName),
  },
});
